import { ChangeDetectionStrategy, Component, HostListener, inject, input } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FloatingPopupResizeDirection } from '@app/main/main-content/floating-popup-manager/enums/floating-popup-resize-direction.enum';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FocusTrapModule } from 'primeng/focustrap';
import { WINDOW } from '@app/core/providers/window/window.token';
import {
  MIN_HEIGHT,
  MIN_WIDTH,
  POPUP_BOUNDARY_CLASS,
} from '@app/main/main-content/floating-popup-manager/configs/floating-popup.config';

@Component({
  selector: 'gc-floating-popup-window',
  standalone: true,
  imports: [CommonModule, CdkDrag, CdkDragHandle, FocusTrapModule, NgClass],
  templateUrl: './floating-popup-window.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloatingPopupWindowComponent {
  private readonly window = inject(WINDOW);
  public readonly service = inject(FloatingPopupService);
  public readonly popupBoundaryClass = `.${POPUP_BOUNDARY_CLASS}`;

  public popup = input.required<IFloatingPopupInstance>();
  protected readonly ResizeDirection = FloatingPopupResizeDirection;

  @HostListener('window:resize')
  onWindowResize() {
    const { x, y, width, height, id } = this.popup();
    let newX = x;
    let newY = y;
    let newWidth = width;
    let newHeight = height;

    const winW = this.window.innerWidth,
      winH = this.window.innerHeight;

    if (width > winW) newWidth = winW - 20;
    if (height > winH) newHeight = winH - 20;
    if (x + width > winW) newX = Math.max(0, winW - width - 10);
    if (y + newHeight > winH) newY = Math.max(0, winH - newHeight - 10);

    this.service.updateState(id, { x: newX, y: newY, width: newWidth, height: newHeight });
  }

  public onDragEnd(event: CdkDragEnd) {
    const pos = event.source.getFreeDragPosition();
    const { x, y, id } = this.popup();

    this.service.updateState(id, { x: x + pos.x, y: y + pos.y });
    event.source.reset();
  }

  public onResizeStart(e: MouseEvent, handle: FloatingPopupResizeDirection) {
    e.preventDefault();
    e.stopPropagation();

    const { x, y, width, height } = this.popup();

    const start = {
      x: e.clientX,
      y: e.clientY,
      w: width,
      h: height,
      px: x,
      py: y,
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      let newW = start.w,
        newH = start.h,
        newX = start.px,
        newY = start.py;
      const dx = moveEvent.clientX - start.x;
      const dy = moveEvent.clientY - start.y;

      if (handle.includes('e')) newW = start.w + dx;
      if (handle.includes('w')) {
        newW = start.w - dx;
        newX = start.px + dx;
      }
      if (handle.includes('s')) newH = start.h + dy;
      if (handle.includes('n')) {
        newH = start.h - dy;
        newY = start.py + dy;
      }

      const { id, minWidth } = this.popup();

      const winW = minWidth || MIN_WIDTH;

      newW = Math.max(newW, winW);
      newH = Math.max(newH, MIN_HEIGHT);

      if (newW >= winW && newX >= 0 && newX + newW <= this.window.innerWidth)
        this.service.updateState(id, { width: newW, x: newX });
      if (newH >= MIN_HEIGHT && newY >= 0 && newY + newH <= this.window.innerHeight)
        this.service.updateState(id, { height: newH, y: newY });
    };

    const onMouseUp = () => {
      this.window.removeEventListener('mousemove', onMouseMove);
      this.window.removeEventListener('mouseup', onMouseUp);
    };
    this.window.addEventListener('mousemove', onMouseMove);
    this.window.addEventListener('mouseup', onMouseUp);
  }
}
